import { CanAccess, useRecordContext, useTranslate } from "ra-core";
import { Navigate } from "react-router";
import { DataTable } from "@/components/admin/data-table";
import { EditButton } from "@/components/admin/edit-button";
import { List } from "@/components/admin/list";
import { SelectField } from "@/components/admin/select-field";

import type { IncentiveProgram } from "../types";
import {
  describeIncentiveProgramParameters,
  formatValidityRange,
  INCENTIVE_PROGRAM_PERIOD_CHOICES,
  INCENTIVE_PROGRAM_SCOPE_CHOICES,
} from "./incentiveProgramForm";

const ValidityField = () => {
  const record = useRecordContext<IncentiveProgram>();
  const translate = useTranslate();
  if (!record) return null;
  return (
    <span className="tabular-nums">
      {formatValidityRange(record, translate)}
    </span>
  );
};

const ParametersSummaryField = () => {
  const record = useRecordContext<IncentiveProgram>();
  const translate = useTranslate();
  if (!record) return null;
  return (
    <span className="tabular-nums">
      {describeIncentiveProgramParameters(record, translate)}
    </span>
  );
};

/**
 * Almikia Epic E7 (TASK-095): the "Incentivos" admin list -- direccion only
 * (`canAccess("incentive_programs", "edit")`, mirroring TASK-087's RLS:
 * every role reads the catalogue, only direccion writes it), every other
 * role redirected home even on a direct URL hit -- same gate shape as
 * `ProjectEdit`/`GoalsPage`. `DataTable`'s built-in skeleton/empty/error
 * states (design-system.md section 8) apply automatically, keyed off the
 * `resources.incentive_programs.empty.*` messages.
 */
export const IncentiveProgramList = () => (
  <CanAccess
    resource="incentive_programs"
    action="edit"
    accessDenied={<Navigate to="/" replace />}
  >
    <List sort={{ field: "code", order: "ASC" }} perPage={25}>
      <DataTable rowClick="edit">
        <DataTable.Col source="name" />
        <DataTable.Col source="period">
          <SelectField
            source="period"
            choices={INCENTIVE_PROGRAM_PERIOD_CHOICES}
          />
        </DataTable.Col>
        <DataTable.Col source="scope">
          <SelectField
            source="scope"
            choices={INCENTIVE_PROGRAM_SCOPE_CHOICES}
          />
        </DataTable.Col>
        <DataTable.Col
          label="resources.incentive_programs.fields.validity"
          source="valid_from"
        >
          <ValidityField />
        </DataTable.Col>
        <DataTable.Col label="resources.incentive_programs.fields.parameters_summary">
          <ParametersSummaryField />
        </DataTable.Col>
        <DataTable.Col label={false}>
          <EditButton />
        </DataTable.Col>
      </DataTable>
    </List>
  </CanAccess>
);
